import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: EASE, delay }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const soldierFor = [
  "Légion Étrangère — 2e REP",
  "Revanche Tactical Group",
  "Private Military Contractor",
  "VIP Protection",
];

export const Hero = () => {
  const ref = useRef(null);
  const { lang, t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src="https://pub-a4d6ad10dc1a4e1da401961c64a9749e.r2.dev/projects/9de52582-87d6-4c3d-abe1-f1f808ef322a/img/35e69571c50df999.png"
          alt="Operatore in comunicazione radio in ambiente marittimo"
          className="w-full h-[130%] object-cover object-center opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D0B] via-[#0A0D0B]/60 to-[#0A0D0B]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D0B]/80 via-transparent to-transparent" />
        <div className="absolute inset-0 crosshair-grid" />
      </motion.div>

      <div className="absolute z-10 top-24 right-4 sm:right-8 lg:right-16 font-mono2 text-[10px] tracking-[0.3em] text-[#5C6A60] flex items-center gap-2" data-testid="hero-coordinates">
        <ShieldCheck className="w-3 h-3 text-[#22C55E]" />
        CLEARANCE: VERIFIED <span className="blink text-[#22C55E]">●</span>
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 px-4 sm:px-8 lg:px-16 pb-10 pt-40">
        <p className="font-mono2 text-xs tracking-[0.35em] text-[#C8B48D] mb-6">
          <MaskedLine key={`e-${lang}`} delay={0.35}>{t.hero.eyebrow}</MaskedLine>
        </p>

        <h1 className="font-heading font-extrabold uppercase tracking-tight leading-[0.95] text-4xl sm:text-6xl lg:text-8xl" data-testid="hero-title">
          <MaskedLine key={`l1-${lang}`} delay={0.5}>{t.hero.line1}</MaskedLine>
          <MaskedLine key={`l2-${lang}`} delay={0.65} className="text-[#C8B48D]">{t.hero.line2}</MaskedLine>
        </h1>

        <p className="mt-6 max-w-xl text-base sm:text-xl text-[#9EACA2] font-light">
          <MaskedLine key={`s-${lang}`} delay={0.85}>{t.hero.sub}</MaskedLine>
        </p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 1.05 }}
          className="mt-8 font-mono2 text-[11px] tracking-[0.2em] text-[#5C6A60] uppercase"
          data-testid="hero-soldier-for"
        >
          <div className="text-[#C8B48D] mb-2">{t.hero.soldierFor}</div>
          {soldierFor.map((s) => (
            <div key={s} className="text-[#9EACA2]">{s}</div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 1.15 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href="#profilo"
            data-testid="hero-cta-dossier"
            className="chamfer-sm bg-[#C8B48D] text-[#0A0D0B] font-mono2 text-xs font-bold tracking-widest px-7 py-4 hover:bg-[#E6ECE8] hover:-translate-y-0.5 transition-all duration-300"
          >
            {t.hero.cta1}
          </a>
          <a
            href="#contatti"
            data-testid="hero-cta-proposal"
            className="chamfer-sm border border-[#5B7052] text-[#E6ECE8] font-mono2 text-xs font-bold tracking-widest px-7 py-4 hover:border-[#C8B48D] hover:text-[#C8B48D] hover:-translate-y-0.5 transition-all duration-300"
          >
            {t.hero.cta2}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 grid grid-cols-2 sm:grid-cols-4 border-t border-[#28342C]"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((s, i) => (
            <div key={s.label} className={`py-5 sm:pr-8 ${i > 0 ? "sm:border-l sm:border-[#28342C] sm:pl-8" : ""}`}>
              <div className="font-heading text-3xl sm:text-4xl font-extrabold text-[#E6ECE8]">{s.value}</div>
              <div className="font-mono2 text-[10px] tracking-[0.25em] text-[#5C6A60] uppercase mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute z-10 bottom-6 left-1/2 -translate-x-1/2 text-[#5C6A60]"
      >
        <ChevronDown className="w-5 h-5" />
      </motion.div>
    </section>
  );
};
